/**
 * Output quality guardrails + marketplace canvas presets (spec 018 / §0.4, §0.5).
 *
 * Quality applies to lossy formats (JPG/WebP) and is clamped to [5,100]; below 50% the UI
 * warns the output may look poor. PNG is lossless — the quality value does not apply.
 * Marketplace presets are square (non-distorting) canvases sized for each marketplace; the
 * exact dimensions are VALIDATE-WITH-ANAND and admins may add custom presets within their
 * `max_canvas_px` limit.
 */
export const QUALITY_MIN = 5;
export const QUALITY_MAX = 100;
export const QUALITY_WARN_BELOW = 50;
export const RECOMMENDED_QUALITY = 90;

export interface OutputPreset {
  name: string;
  width: number;
  height: number;
  format: string;
  quality: number;
}

export function clampQuality(q: number): number {
  if (Number.isNaN(q)) return RECOMMENDED_QUALITY;
  return Math.max(QUALITY_MIN, Math.min(QUALITY_MAX, Math.round(q)));
}

/** A warning string when quality is low enough to look poor, else null. */
export function qualityWarning(q: number): string | null {
  return q < QUALITY_WARN_BELOW
    ? `Quality ${q}% is low — the output may look visibly compressed.`
    : null;
}

/** PNG is lossless; the quality percentage doesn't apply (optimized compression instead). */
export function isLossless(format: string): boolean {
  return format.toLowerCase() === "png";
}

/** Built-in marketplace canvas presets (square → product is never distorted). */
export const MARKETPLACE_PRESETS: OutputPreset[] = [
  { name: "Amazon 2000²", width: 2000, height: 2000, format: "jpg", quality: 90 },
  { name: "Amazon 2100²", width: 2100, height: 2100, format: "jpg", quality: 90 },
  { name: "Flipkart 2000²", width: 2000, height: 2000, format: "jpg", quality: 90 },
  { name: "Etsy 2700²", width: 2700, height: 2700, format: "png", quality: 100 },
];
